/**
 * HandoffActions — Story 12.3 / Epic E12.
 *
 * Botões mutuamente exclusivos:
 *   - "Assumir conversa" quando status ∈ ROUTABLE
 *   - "Devolver pra Rafa" quando status === 'human_handoff'
 *
 * Status terminais (closed, error, converted, declined, inactive) → nenhum.
 *
 * Endpoints:
 *   - POST /admin/conversations/:id/handoff       → 200 / 409 RACE / 404
 *   - POST /admin/conversations/:id/return-to-ai  → 200 / 409 NOT_IN_HANDOFF
 *
 * Em 409 (race), refetch silencioso pra UI mostrar estado atual.
 */

import { useMutation } from '@tanstack/react-query';
import { Bot, User } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/sonner';
import { ApiError, apiClient } from '@/lib/api';
import type { ConversationStatus } from '@/components/conversations/StatusBadge';

/**
 * Status que aceitam transição → `human_handoff`. Inclui ROUTABLE +
 * `'error'` (caso operacional: IA quebrou, time humano resgata o lead).
 * Espelha `ASSUMABLE_STATUSES` no backend (`ConversationHandoffService`).
 */
const ASSUMABLE_STATUSES: ReadonlySet<ConversationStatus> = new Set([
  'active',
  'awaiting_response',
  'scheduled_callback',
  'error',
]);

export interface HandoffActionsProps {
  conversationId: string;
  status: ConversationStatus | 'human_handoff';
  /** Disparado após mutation OK ou race detectada (refetch dispatch). */
  onChange: () => void;
}

function humanizeError(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: { code?: string; message?: string } } | undefined;
    if (body?.error?.code === 'RACE_CONDITION' || err.status === 409) {
      return 'Estado da conversa mudou — atualizando…';
    }
    if (err.status === 403) return 'Você não tem permissão pra essa ação.';
    return body?.error?.message ?? `Falha (HTTP ${err.status}).`;
  }
  return err instanceof Error ? err.message : 'Falha inesperada.';
}

export function HandoffActions({ conversationId, status, onChange }: HandoffActionsProps) {
  const isHandoff = status === 'human_handoff';
  const isAssumable = ASSUMABLE_STATUSES.has(status as ConversationStatus);

  const assumeMutation = useMutation({
    mutationFn: () =>
      apiClient.post<{ id: string; status: 'human_handoff' }>(
        `/admin/conversations/${conversationId}/handoff`,
        {},
      ),
    onSuccess: () => {
      toast.success('Você assumiu a conversa. A Rafa parou de responder aqui.');
      onChange();
    },
    onError: (err) => {
      toast.error(humanizeError(err));
      onChange();
    },
  });

  const returnMutation = useMutation({
    mutationFn: () =>
      apiClient.post<{ id: string; status: 'active' }>(
        `/admin/conversations/${conversationId}/return-to-ai`,
        {},
      ),
    onSuccess: () => {
      toast.success('Conversa devolvida pra Rafa.');
      onChange();
    },
    onError: (err) => {
      toast.error(humanizeError(err));
      onChange();
    },
  });

  if (isAssumable) {
    return (
      <Button
        type="button"
        variant="default"
        onClick={() => assumeMutation.mutate()}
        disabled={assumeMutation.isPending}
        data-testid="assume-handoff-button"
        aria-label="Assumir conversa e pausar a Rafa"
      >
        <User aria-hidden className="mr-1 h-3 w-3" />
        {assumeMutation.isPending ? 'Assumindo…' : 'Assumir conversa'}
      </Button>
    );
  }

  if (isHandoff) {
    return (
      <Button
        type="button"
        variant="outline"
        onClick={() => returnMutation.mutate()}
        disabled={returnMutation.isPending}
        data-testid="return-to-ai-button"
        aria-label="Devolver conversa pra Rafa"
      >
        <Bot aria-hidden className="mr-1 h-3 w-3" />
        {returnMutation.isPending ? 'Devolvendo…' : 'Devolver pra Rafa'}
      </Button>
    );
  }

  return null;
}

export default HandoffActions;
